/**
 * 안드로이드 앱(`android-urnplay.com`)과 주고받는 통로. (2026-10-08)
 *
 * 앱의 웹뷰는 urnplay.com 페이지에만 `window.UrnplayApp` 을 붙여 준다. 일반 브라우저에는
 * 없으므로 `isApp()` 이 false 고, 아래 함수들은 아무것도 하지 않는다.
 *
 * 규약은 앱 쪽 `AppBridge.kt` 와 짝이다 — 한쪽을 바꾸면 다른 쪽도 같이 바꾼다.
 *   페이지 → 앱   `{id, type, ...}`              답이 필요한 요청 (같은 id 로 답이 온다)
 *                 `{type: 'media', ...}`          알리기만 한다
 *   앱 → 페이지   `{id, ok, data | error}`        요청에 대한 답
 *                 `{event, ...}`                  앱이 먼저 보내는 알림 (알림창의 일시정지 등)
 *
 * ★ 왜 앱이 필요한가
 *   - 구글은 웹뷰 안의 로그인을 막는다(`disallowed_useragent`). 앱이 안드로이드 기본
 *     구글 로그인으로 id_token 을 받아 넘긴다. 토큰의 aud 가 웹 클라이언트 ID 라서
 *     백엔드 `google.json` 은 그대로 받는다.
 *   - 웹뷰에는 `navigator.share` 가 없다. 앱의 공유 시트를 쓴다.
 */

interface AppMessenger {
  postMessage(message: string): void;
  addEventListener(type: 'message', listener: (event: { data: string }) => void): void;
}

declare global {
  interface Window {
    UrnplayApp?: AppMessenger;
  }
}

interface AppReply {
  id: number;
  ok: boolean;
  data?: unknown;
  error?: string;
}

interface AppEvent {
  event: string;
  [key: string]: unknown;
}

type EventHandler = (event: AppEvent) => void;

let nextId = 1;
let listening = false;
const pending = new Map<
  number,
  { resolve: (data: unknown) => void; reject: (err: Error) => void }
>();
const eventHandlers = new Map<string, Set<EventHandler>>();

/** 앱 안에서 열렸는가 */
export function isApp(): boolean {
  return typeof window !== 'undefined' && !!window.UrnplayApp;
}

function listen(): void {
  if (listening || !window.UrnplayApp) return;
  listening = true;

  window.UrnplayApp.addEventListener('message', ({ data }) => {
    let message: AppReply | AppEvent;
    try {
      message = JSON.parse(data) as AppReply | AppEvent;
    } catch {
      return;
    }

    if ('event' in message) {
      eventHandlers.get(message.event)?.forEach((handler) => handler(message as AppEvent));
      return;
    }

    const waiter = pending.get(message.id);
    if (!waiter) return;
    pending.delete(message.id);

    if (message.ok) waiter.resolve(message.data);
    else waiter.reject(new Error(message.error ?? '앱에서 처리하지 못했습니다.'));
  });
}

/** 앱에 묻고 답을 기다린다. 앱이 아니면 바로 실패한다. */
export function appRequest<T>(type: string, payload: Record<string, unknown> = {}): Promise<T> {
  const app = window.UrnplayApp;
  if (!app) return Promise.reject(new Error('앱이 아닙니다.'));
  listen();

  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (data: unknown) => void, reject });
    app.postMessage(JSON.stringify({ ...payload, id, type }));
  });
}

/** 앱에 알리기만 한다. 앱이 아니면 아무 일도 없다. */
export function appNotify(type: string, payload: Record<string, unknown> = {}): void {
  const app = window.UrnplayApp;
  if (!app) return;
  listen();
  app.postMessage(JSON.stringify({ ...payload, type }));
}

/** 앱이 먼저 보내는 알림을 받는다. 돌려받은 함수를 부르면 그만 받는다. */
export function onAppEvent(event: string, handler: EventHandler): () => void {
  listen();

  let handlers = eventHandlers.get(event);
  if (!handlers) {
    handlers = new Set();
    eventHandlers.set(event, handlers);
  }
  handlers.add(handler);

  return () => handlers.delete(handler);
}

/** 사용자가 고르기를 그만둔 것 — 오류로 알리지 않는다 */
export function isAppCancel(err: unknown): boolean {
  return err instanceof Error && err.message === 'cancelled';
}
